import { useQuery } from '@tanstack/react-query'
import { recipesCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Link } from '@/ui/Link/Link'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { RowList, RowListItem } from '@/ui/RowList/RowList'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { StatusBadge } from '@/ui/Status/Status'
import { recipesApi } from '../../api/recipes'
import { ROUTES } from '../../constants/routes'

interface Props {
  workspaceId: string
  resourceId: string
}

/**
 * What a source is used for: the recipes set up on it, each linking to its
 * setup on the Recipes page. Read-only — recipes are managed there.
 */
export function RecipeUsageCard({ workspaceId, resourceId }: Props) {
  const query = useQuery({
    queryKey: ['recipe-installs', workspaceId, { source: resourceId }],
    queryFn: () => recipesApi.installs(workspaceId, { source: resourceId }),
  })
  const showSkeleton = useSkeletonDelay(query.isPending)
  const installs = query.data ?? []
  return (
    <Stack as="section" gap="space-12" aria-busy={query.isPending}>
      <SectionHeader rule trailingSpacing="compact">
        {recipesCopy.usage.heading}
      </SectionHeader>
      {query.isPending ? (
        showSkeleton && <Skeleton lines={2} />
      ) : query.isError ? (
        <Alert tone="danger">
          <Inline gap="space-8" align="baseline">
            {recipesCopy.usage.loadFailed}
            <Button
              variant="outline"
              size="sm"
              onClick={() => void query.refetch()}
            >
              {recipesCopy.retry}
            </Button>
          </Inline>
        </Alert>
      ) : installs.length === 0 ? (
        <EmptyState
          variant="inline"
          description={recipesCopy.usage.empty}
          action={
            <Button
              variant="outline"
              href={ROUTES.workspaceRecipes(workspaceId)}
            >
              {recipesCopy.usage.setUp}
            </Button>
          }
        />
      ) : (
        <RowList variant="plain" hover={false}>
          {installs.map((i) => (
            <RowListItem key={i.id}>
              <Link href={ROUTES.recipeInstall(workspaceId, i.id)}>
                {i.recipe_title}
              </Link>
              <Text variant="caption" tone="text-mute">
                {recipesCopy.install.topicName(
                  i.topic_name ?? recipesCopy.install.topicFallback,
                )}
              </Text>
              {!i.enabled && (
                <StatusBadge tone="warning">
                  {recipesCopy.usage.paused}
                </StatusBadge>
              )}
            </RowListItem>
          ))}
        </RowList>
      )}
    </Stack>
  )
}
